import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { Button, Form } from "react-bootstrap";
import moment from "moment";
import { useFormik } from "formik";
import { DateRangePicker, Stack } from "rsuite";
import { BiSearch, BiReset, BiArrowBack } from "react-icons/bi";
import { BsCloudDownloadFill } from "react-icons/bs";
import Sidebar from "../../includes/Sidebar";
import { downloadXl, predefinedBottomRanges, convertDateToStr } from "../../../functions";
import { Tooltip } from "@mui/material";
import { seErrorMessage } from "../../../slice/error/errorSlice";
import { useDispatch } from "react-redux";
import PartnerDropDown from "../../components/PartnerDropDown";
import ClientDropDown from "../../components/ClientDropDown";
import { SftpHouseKeepingService } from "../../../services/SftpHouseKeepingService";
import Select from "react-select";

const datatableCustomStyles = {
  rows: {
    style: {
      minHeight: '20px',
      fontSize: '0.7em',
      fontColor: '#212529 !important',
      width: '100%'
    },
  },
  headCells: {
    style: {
      paddingLeft: '5px',
      paddingRight: '5px',
      textTransform: "uppercase",
      fontSize: '0.7em',
      fontWeight: '800',
      fontColor: '#6c757d !important',
      opacity: 1,
      minHeight: "10px",
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      width: "100%",
    },
  },
  pagination: {
    style: {
      color: '#000',
      fontSize: '13px',
      minHeight: '56px',
      backgroundColor: '#fff',
      borderTopStyle: 'solid',
      borderTopWidth: '1px',
      borderTopColor: '#f2f2f2',
    },
    pageButtonsStyle: {
      borderRadius: '50%',
      height: '40px',
      width: '40px',
      padding: '8px',
      margin: '0px',
      cursor: 'pointer',
      transition: '0.4s',
      color: '#000',
      fill: '#000',
      backgroundColor: 'transparent',
      '&:disabled': {
        cursor: 'unset',
        color: '#e2e2e2',
        fill: '#e2e2e2',
      },
      '&:hover:not(:disabled)': {
        backgroundColor: '#f2f2f2',
      },
      '&:focus': {
        outline: 'none',
        backgroundColor: '#f5f5f5',
      },
    },
  },
};

const statuses = [
  { value: "pending", label: "Pending" },
  { value: "deleted", label: "Deleted" },
  { value: "failed", label: "Failed" },
];

const selectStyles = {
  container: (styles) => ({
    ...styles,
    borderRadius: "9px",
    width: "100%"
  }),
  control: (styles, state) => ({
    ...styles,
    backgroundColor: "hsl(204deg 33.33% 97.06%)",
    borderStyle: "none",
    boxShadow: "none",
    borderRadius: "10px",
    maxHeight: "30px",
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    width: "0px",
    minHeight: "20px",
    maxHeight: "30px",
  }),
  valueContainer: (styles) => ({
    ...styles,
    maxHeight: "30px",
    overflow: "scroll",
  }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isSelected ? "#dbf4f5" : "inherit",
    color: state.isSelected ? "#000000" : "inherit",
    "&:hover": { backgroundColor: state.isSelected ? "#dbf4f5" : "rgb(222, 235, 255)" },
  }),
};

function SftpHouseKeeping() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const [pageno, setPageNo] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [colorRow, setColorRow] = useState({});
  const [data, setData] = useState({});
  const [limit, setLimit] = useState(100);
  const [searchedData, setSearchedData] = useState({
    partners: [],
    clients: [],
    filename: "",
    folder: "",
    status: [],
    start_date_range: "",
    end_date_range: "",
    started_at_range: "",
    completed_at_range: "",
    show_all_records:false,
  });

  const [isInitSearch, setIsInitSearch] = useState(true);
  const [isFocusedStatus, setIsFocusedStatus] = useState(false);

  const sftpHouseKeepingService = new SftpHouseKeepingService();
  const { getSftpHouseKeepingLogs } = sftpHouseKeepingService;

  const handleRowClick = (row) => {
    setColorRow(row);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setLimit(newPerPage);
    setPageNo(1);
    fetchData(1, newPerPage, searchedData);
  };

  const handlePageChange = (page) => {
    setPageNo(page);
    fetchData(page, limit, searchedData);
  };

  const fetchData = (page, limit, values, export_type = false, header_names = []) => {
    if (export_type) {
      setExportLoading(true);
    } else {
      setLoading(true);
    }

    const start_date_range = values.start_date_range ? convertDateToStr(values.start_date_range, false) : "";
    const end_date_range = values.end_date_range ? convertDateToStr(values.end_date_range, false) : "";
    const started_at_range = values.started_at_range ? convertDateToStr(values.started_at_range, true) : "";
    const completed_at_range = values.completed_at_range ? convertDateToStr(values.completed_at_range, true) : "";

    getSftpHouseKeepingLogs({
      id,
      page,
      limit,
      partners: values.partners,
      clients: values.clients,
      filename: values.filename,
      folder: values.folder,
      status: values.status,
      show_all_records:values.show_all_records||false,
      start_date_range,
      end_date_range,
      started_at_range,
      completed_at_range,
      export_type,
      header_names
    }).then((response) => {
      if (export_type) {
        setExportLoading(false);
        const url = response?.data?.export_file || response?.data?.url || response?.data;
        if (url) {
          downloadXl(url);
        } else {
          dispatch(seErrorMessage({ message: "Export failed: No download URL returned", is_show: true }));
        }
      } else {
        setData(response?.data || {});
        setLimit(limit);
        setPageNo(page);
        setTotalRows(response?.data?.count || 0);
        setLoading(false);
      }
    }).catch((err) => {
      setLoading(false);
      setExportLoading(false);
      dispatch(seErrorMessage({ message: "An error occurred while fetching reports data.", is_show: true }));
    });
  };

  const searchFormik = useFormik({
    initialValues: {
      partners: [],
      clients: [],
      filename: "",
      folder: "",
      status: [],
      start_date_range: "",
      end_date_range: "",
      started_at_range: "",
      completed_at_range: "",
      show_all_records: false
    },
    onSubmit: (values) => {
      setIsInitSearch(false);
      setSearchedData(values);
      fetchData(1, limit, values);
    }
  });

  const handleReset = () => {
    searchFormik.resetForm({
      values: {
        partners: [],
        clients: [],
        filename: "",
        folder: "",
        status: [],
        start_date_range: "",
        end_date_range: "",
        started_at_range: "",
        completed_at_range: "",
        show_all_records:false
      }
    });
    setIsInitSearch(true);
    setData({});
    setTotalRows(0);
    setPageNo(1);
    setSearchedData({
      partners: [],
      clients: [],
      filename: "",
      folder: "",
      status: [],
      start_date_range: "",
      end_date_range: "",
      started_at_range: "",
      completed_at_range: "",
      show_all_records:false
    });
  };

 

  const handleOnDateTimeChange = ({ name, value }) => {
    searchFormik.setFieldValue(name, value ?? "");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (id) {
      setIsInitSearch(false);
      fetchData(1, limit, searchFormik.values);
    }
  }, [id]);

  const columns = useMemo(
    () => [
      {
        id: 'S.NO',
        name: 'S.NO',
        selector: (row, index) =>
          Number(pageno) > 1
            ? (Number(pageno) - 1) * Number(limit) + index + 1
            : index + 1,
        wrap: true,
        width: "50px",
        grow: 0.1,
      },
      {
        id: 'partner_name',
        name: 'Partner Name',
        exportKey: 'PARTNER NAME',
        selector: row => row?.partner_name,
        cell: (row) => <span className="text-truncate" style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.partner_name}</span>,
        left: true,
        wrap: true,
        maxWidth: "200px"
        
      },
      {
        id: 'client_name',
        name: 'Client Name',
        exportKey: 'CLIENT NAME',
        selector: row => row?.client_name,
        cell: (row) => <span className="text-truncate" style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.client_name}</span>,
        left: true,
        wrap: true,
        maxWidth: "200px"
      },
      {
        id: 'status',
        name: 'Status',
        exportKey: 'STATUS',
        selector: row => row?.status,
        cell: (row) => <span className="text-truncate" style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.status}</span>,
        left: true,
        wrap: true,
        width: "100px",
      },
      {
        id: 'total_files',
        name: 'Total Files',
        exportKey: 'TOTAL FILES',
        selector: row => row?.total_files,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.total_files}</span>,
        left: true,
        wrap: true,
        width: "100px",
      },
      {
        id: 'total_size',
        name: 'Total Size',
        exportKey: 'TOTAL SIZE',
        selector: row => row?.total_size,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.total_size}</span>,
        left: true,
        wrap: true,
        width: "100px",
      },
      {
        id: 'date',
        name: 'Date',
        exportKey: 'DATE',
        selector: row => row?.date,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.date}</span>,
        left: true,
        wrap: true,
        width: "120px",
      },

      {
        id: 'started_at',
        name: 'Deletion Start Date',
        exportKey: 'DELETION START DATE',
        selector: row => row?.started_at,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.started_at}</span>,
        left: true,
        wrap: true,
        width: "120px",
      },
      {
        id: 'completed_at',
        name: 'Deletion End Date',
        exportKey: 'DELETION END DATE',
        selector: row => row?.completed_at,
        cell: (row) => <span style={{ width: "100%" }} onClick={() => handleRowClick(row)}>{row?.completed_at}</span>,
        left: true,
        wrap: true,
        width: "120px",
      },
      {
        id: 'folder',
        name: 'Folder',
        exportKey: 'FOLDER',
        selector: row => row?.folder,
        cell: (row) => (
          <span
            className="text-truncate"
            style={{ width: "100%", maxWidth: "300px" }}
            title={row?.folder}
            onClick={() => handleRowClick(row)}
          >
            {row?.folder}
          </span>
        ),
        left: true,
        wrap: true,
      }
    ],
    [limit, pageno, colorRow]
  );


  const handleExport = () => {
    const headers = columns.filter(i => i.exportKey !== undefined).map(i => i.exportKey);
    fetchData(pageno, limit, searchFormik.values, true, headers);
  };

  const conditionalRowStyles = [
    {
      when: row => colorRow.id === row.id,
      style: () => ({
        backgroundColor: '#dbf4f5',
        color: 'black',
      }),
    },
  ];

  return (
    <React.Fragment>
      <Sidebar />
      <main id="main" className="main">
        <div className="pagetitle tab-fixed-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          {/* {id && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-flex align-items-center gap-1"
              style={{ fontSize: "0.85em" }}
              onClick={() => navigate(-1)}
            >
              <BiArrowBack /> Back to Logs
            </Button>
          )} */}
          <h1 id='page-title'>SFTP House Keeping - Detailed Log</h1>
          {data?.deleted_file_size && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              backgroundColor: '#fff3cd', color: '#856404',
              border: '1px solid #ffc107', borderRadius: '6px',
              padding: '3px 10px', fontSize: '0.78em', fontWeight: '600',
              whiteSpace: 'nowrap'
            }}>
              <span style={{ fontSize: '0.9em' }}>🗑️</span>
              Deleted File Size: <strong>{data?.deleted_file_size}</strong>
            </span>
          )}
          {data?.pending_file_size && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              backgroundColor: '#d1ecf1', color: '#0c5460',
              border: '1px solid #17a2b8', borderRadius: '6px',
              padding: '3px 10px', fontSize: '0.78em', fontWeight: '600',
              whiteSpace: 'nowrap'
            }}>
              <span style={{ fontSize: '0.9em' }}>⏳</span>
              Pending File Size: <strong>{data?.pending_file_size}</strong>
            </span>
          )}
        </div>

        <section className="section mt-5 ">
          <div className="row align-items-top py-2 gap-2 gap-md-5">
            <div className="col-lg-12">
              <div className="card mb-5 mb-xl-8 mt-3">
                <Form onSubmit={searchFormik.handleSubmit} onReset={handleReset}>
                  <div className=" ">
                    <div className="px-1 py-1 d-flex pe-2 flex-row gap-2 align-items-center flex-wrap">
                      <PartnerDropDown lockboxformik={searchFormik} width="200px" />
                      <ClientDropDown lockboxformik={searchFormik} width="200px" />

                      <div style={{ width: '200px', position: 'relative' }}>
                        <Tooltip disableInteractive title={"Folder"} placement="top" arrow>
                          <Form.Group>
                            <Form.Control
                              type="search"
                              placeholder="Folder"
                              name="folder"
                              className="form-control form-control-solid b-r-10"
                              onChange={searchFormik.handleChange}
                              value={searchFormik.values.folder || ""}
                              style={{
                                borderRadius: '10px',
                                height: '38px',
                                backgroundColor: 'hsl(204, 33%, 97%)',
                                borderStyle: 'none',
                                fontSize: '12px',
                                paddingRight: searchFormik.values.folder ? '28px' : undefined
                              }}
                            />
                            {searchFormik.values.folder && (
                              <button
                                type="button"
                                onClick={() => searchFormik.setFieldValue('folder', '')}
                                style={{
                                  position: 'absolute',
                                  right: '8px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  background: 'none',
                                  border: 'none',
                                  color: '#999',
                                  cursor: 'pointer',
                                  padding: '0',
                                  lineHeight: '1',
                                  fontSize: '14px'
                                }}
                              >
                                &times;
                              </button>
                            )}
                          </Form.Group>
                        </Tooltip>
                      </div>

                      <div style={{ width: '200px', position: 'relative' }}>
                        <Select
                          isMulti
                          options={statuses}
                          className="select-search"
                          classNamePrefix="select"
                          placeholder="Select Status"
                          isClearable
                          closeMenuOnSelect={false}
                          hideSelectedOptions={true}
                          name="status"
                          onChange={(selected) => {
                            const values = selected ? selected.map(option => option.value) : [];
                            searchFormik.setFieldValue("status", values);
                          }}
                          value={statuses.filter(option =>
                            searchFormik.values.status?.includes(option.value)
                          )}
                          onFocus={() => setIsFocusedStatus(true)}
                          onBlur={() => setIsFocusedStatus(false)}
                          styles={{
                            ...selectStyles,
                            control: (styles, state) => ({
                              ...selectStyles.control(styles, state),
                              maxHeight: isFocusedStatus ? "auto" : "30px",
                            }),
                            valueContainer: (styles, state) => ({
                              ...selectStyles.valueContainer(styles, state),
                              maxHeight: isFocusedStatus ? "auto" : "30px",
                              overflow: isFocusedStatus ? "visible" : "scroll",
                            }),
                          }}
                        />
                      </div>

                      <Tooltip disableInteractive title={"Date Range"} placement="top" arrow>
                        <div className="correspondenceDatePicker">
                          <Stack direction="column" spacing={8} alignItems="flex-start">
                            <DateRangePicker
                              ranges={predefinedBottomRanges}
                              cleanable={true}
                              placement="auto"
                              appearance="default"
                              format={"MM/dd/yyyy"}
                              className="form-date-control form-control-solid b-r-1 text-dark"
                              placeholder={"MM/DD/YYYY  - MM/DD/YYYY "}
                              name="start_date_range"
                              onChange={(e) => { handleOnDateTimeChange({ name: 'start_date_range', value: e }) }}
                              value={searchFormik?.values?.start_date_range || null}
                            />
                          </Stack>
                        </div>
                      </Tooltip>

                      {/* <Tooltip disableInteractive title={"End Date Range"} placement="top" arrow>
                        <div className="correspondenceDatePicker">
                          <Stack direction="column" spacing={8} alignItems="flex-start">
                            <DateRangePicker
                              ranges={predefinedBottomRanges}
                              cleanable={true}
                              placement="auto"
                              appearance="default"
                              format={"MM/dd/yyyy"}
                              className="form-date-control form-control-solid b-r-1 text-dark"
                              placeholder={"MM/DD/YYYY  - MM/DD/YYYY "}
                              name="end_date_range"
                              onChange={(e) => { handleOnDateTimeChange({ name: 'end_date_range', value: e }) }}
                              value={searchFormik?.values?.end_date_range || null}
                            />
                          </Stack>
                        </div>
                      </Tooltip> */}

                      <Tooltip disableInteractive title={"Deletion Start Date Range"} placement="top" arrow>
                        <div className="correspondenceDatePicker">
                          <Stack direction="column" spacing={8} alignItems="flex-start">
                            <DateRangePicker
                              ranges={predefinedBottomRanges}
                              defaultCalendarValue={[new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)]}
                              cleanable={true}
                              placement="auto"
                              appearance="default"
                              format={"MM/dd/yyyy HH:mm:ss"}
                              className="form-date-control form-control-solid b-r-1 text-dark"
                              placeholder={"MM/DD/YYYY - MM/DD/YYYY"}
                              name="started_at_range"
                              onChange={(e) => { handleOnDateTimeChange({ name: 'started_at_range', value: e }) }}
                              value={searchFormik?.values?.started_at_range || null}
                            />
                          </Stack>
                        </div>
                      </Tooltip>

                      <Tooltip disableInteractive title={"Deletion End Date Range"} placement="top" arrow>
                        <div className="correspondenceDatePicker">
                          <Stack direction="column" spacing={8} alignItems="flex-start">
                            <DateRangePicker
                              ranges={predefinedBottomRanges}
                              cleanable={true}
                              placement="auto"
                              appearance="default"
                              format={"MM/dd/yyyy HH:mm:ss"}
                              defaultCalendarValue={[new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)]}                                                                                         
                              className="form-date-control form-control-solid b-r-1 text-dark"
                              placeholder={"MM/DD/YYYY  - MM/DD/YYYY "}
                              name="completed_at_range"
                              onChange={(e) => { handleOnDateTimeChange({ name: 'completed_at_range', value: e }) }}
                              value={searchFormik?.values?.completed_at_range || null}
                            />
                          </Stack>
                        </div>
                      </Tooltip>
                      
                      <div className="d-flex align-items-center gap-2 sftp-house-keep-switch">
                        <Tooltip disableInteractive title={"Show All Records"} placement="top" arrow>
                          <Form.Check
                            type="switch"
                            id="show_all_records_switch"
                            checked={searchFormik?.values?.show_all_records || false}
                            onChange={(e) => searchFormik.setFieldValue("show_all_records", e.target.checked)}
                            className="mb-0"
                          />
                        </Tooltip>
                      </div>

                      <div>
                        <Tooltip disableInteractive title={"Search"} placement="top" arrow>
                          <Button className="btn btn-light btn-active-primary" type="submit" disabled={loading}>
                            <span className="svg-icon svg-icon-3">
                              {loading ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <BiSearch />
                              )}
                            </span>
                          </Button>
                        </Tooltip>
                      </div>

                      <div>
                        <Tooltip disableInteractive title={"Reset"} placement="top" arrow>
                          <Button className="btn btn-light btn-active-primary" type="reset" disabled={loading}>
                            <span className="svg-icon svg-icon-3">
                              <BiReset />
                            </span>
                          </Button>
                        </Tooltip>
                      </div>

                      <div>
                        <Tooltip disableInteractive title={"Download"} placement="top" arrow>
                          <Button
                            className="btn btn-light btn-active-primary"
                            type="button"
                            onClick={handleExport}
                            disabled={exportLoading || loading}
                          >
                            {exportLoading ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                              <span className="svg-icon svg-icon-3">
                                <BsCloudDownloadFill />
                              </span>
                            )}
                          </Button>
                        </Tooltip>
                      </div>

                    </div>
                  </div>
                </Form>

                <div className="card-body p-common">
                  <div className="table-responsive">
                    <div className="dashboardtable">
                      <DataTable
                        className="custom-datatable"
                        paginationDefaultPage={pageno}
                        fixedHeader={true}
                        progressPending={loading}
                        data={data?.results}
                        columns={columns}
                        pointerOnHover={true}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={limit}
                        paginationRowsPerPageOptions={[25, 50, 100, 150, 200]}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        conditionalRowStyles={conditionalRowStyles}
                        theme="solarized"
                        customStyles={datatableCustomStyles}
                        striped={true}
                        noDataComponent={isInitSearch ? <p className="p-4">Apply filters to refine and display the data</p> : <p className="p-4">There are no records to display</p>}
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  );
}

export default SftpHouseKeeping;
